import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import api from '../lib/api';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import ForceGraph2D from 'react-force-graph-2d';
import { forceRadial, forceCollide } from 'd3-force';
import {
  ZoomIn, ZoomOut, Maximize2, Layers, User, Users,
  MousePointerClick, Minus, ChevronRight, X, Palette, Lock
} from 'lucide-react';

const PERSON_PATH = new Path2D(
  'M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z'
);

const COLORS = [
  '#5AA2AE', '#9b87f5', '#22c55e', '#f59e0b', '#ef4444',
  '#6366f1', '#ec4899', '#14b8a6', '#8b5cf6', '#f97316'
];
const COLORS_LIGHT = [
  '#7dd3fc', '#c4b5fd', '#86efac', '#fcd34d', '#fca5a5',
  '#a5b4fc', '#f9a8d4', '#5eead4', '#c084fc', '#fdba74'
];

const MODES = {
  INDIVIDUAL: 'individual',
  LEVEL_1: 'level1',
  LEVEL_2: 'level2',
  LEVEL_3: 'level3'
};

const NODE_R = 4;
const ANIM_STEPS = 18;
const GOLDEN = Math.PI * (3 - Math.sqrt(5));
const INSUFFICIENT_DATA_COLOR = '#9ca3af';

/* ─── apply alpha to any color string (hex or rgb) ─── */
function withAlpha(color, alpha) {
  if (!color) return `rgba(0,0,0,${alpha})`;
  const m = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
  if (m) return `rgba(${m[1]}, ${m[2]}, ${m[3]}, ${alpha})`;
  // hex
  if (color.startsWith('#') && color.length >= 7) {
    const r = parseInt(color.slice(1, 3), 16);
    const g = parseInt(color.slice(3, 5), 16);
    const b = parseInt(color.slice(5, 7), 16);
    return `rgba(${r}, ${g}, ${b}, ${alpha})`;
  }
  return color;
}

/* ─── color-by score gradient: red → yellow → green ─── */
function getScoreColor(score, min = 0, max = 10) {
  if (score === null || score === undefined) return null;
  const ratio = Math.max(0, Math.min(1, (score - min) / (max - min)));
  if (ratio < 0.5) {
    const r = 239;
    const g = Math.round(68 + (ratio * 2) * (163 - 68));
    return `rgb(${r}, ${g}, 68)`;
  }
  const r = Math.round(234 - ((ratio - 0.5) * 2) * (234 - 34));
  const g = Math.round(179 + ((ratio - 0.5) * 2) * (197 - 179));
  const b = Math.round(8 + ((ratio - 0.5) * 2) * (94 - 8));
  return `rgb(${r}, ${g}, ${b})`;
}

/* ─── helpers ─── */

function spiralPositions(count, cx, cy, maxR) {
  if (count === 0) return [];
  if (count === 1) return [{ x: cx, y: cy }];
  return Array.from({ length: count }, (_, i) => {
    const a = i * GOLDEN;
    const r = maxR * Math.sqrt(i / (count - 1)) * 0.88;
    return { x: cx + Math.cos(a) * r, y: cy + Math.sin(a) * r };
  });
}

function groupRadius(memberCount, childR, expanded) {
  if (!expanded) return 14 + Math.sqrt(memberCount) * 2.2;
  if (memberCount === 0) return 18;
  const gap = 1.8;
  return Math.max(22, Math.sqrt(memberCount) * (childR + gap) * 1.15 + 6);
}

function circularPositions(items, cx, cy, baseR) {
  if (items.length === 0) return [];
  if (items.length === 1) return [{ x: cx, y: cy }];
  const total = items.reduce((s, it) => s + (it.r || 20) * 2 + 6, 0);
  const R = Math.max(baseR, total / (2 * Math.PI));
  let angle = -Math.PI / 2;
  return items.map(it => {
    const span = ((it.r || 20) * 2 + 6) / R;
    const a = angle + span / 2;
    angle += span;
    return { x: cx + Math.cos(a) * R, y: cy + Math.sin(a) * R };
  });
}

function easeInOutQuad(t) {
  return t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t;
}

/* ═══════════════════ COMPONENT ═══════════════════ */

export default function UserGalaxyPage2() {
  const { projectId } = useParams();
  const { user } = useAuth();
  const graphRef = useRef();
  const containerRef = useRef();
  const animRef = useRef(null);
  const posRef = useRef(new Map()); // stores full node objects for repainting
  const nodeDataRef = useRef(new Map()); // stores full node data

  const [loading, setLoading] = useState(true);
  const [segmentTypes, setSegmentTypes] = useState([]);
  const [segments, setSegments] = useState([]);
  const [stakeholders, setStakeholders] = useState([]);
  const [selections, setSelections] = useState([]);
  const [currentSH, setCurrentSH] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [responses, setResponses] = useState([]);
  const [colorByQuestion, setColorByQuestion] = useState(null);
  const [anonymityThreshold, setAnonymityThreshold] = useState(3);
  const [segmentScores, setSegmentScores] = useState({});

  const [mode, setMode] = useState(MODES.INDIVIDUAL);
  const [typeId1, setTypeId1] = useState(null);
  const [typeId2, setTypeId2] = useState(null);
  const [typeId3, setTypeId3] = useState(null);
  const [expanded, setExpanded] = useState(new Map());
  const [hoveredId, setHoveredId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [drillDownMenu, setDrillDownMenu] = useState(null);
  const [dims, setDims] = useState({ w: 800, h: 600 });
  const [animating, setAnimating] = useState(false);
  const [graphData, setGraphData] = useState({ nodes: [], links: [] });
  const forcesConfigured = useRef(false);
  const initialZoomDone = useRef(false);

  /* ─── container size ─── */
  useEffect(() => {
    const update = () => {
      if (!containerRef.current) return;
      const r = containerRef.current.getBoundingClientRect();
      if (r.width > 0) {
        setDims({
          w: Math.max(400, r.width),
          h: Math.max(450, Math.min(700, window.innerHeight - 280))
        });
      }
    };
    const t = setTimeout(update, 100);
    window.addEventListener('resize', update);
    return () => { clearTimeout(t); window.removeEventListener('resize', update); };
  }, [loading]);

  /* ─── fetch data ─── */
  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        setLoading(true);
        const [tRes, sRes, shRes, selRes, qRes, rRes, cfgRes, permsRes] = await Promise.all([
          api.get(`/projects/${projectId}/segment-types`),
          api.get(`/projects/${projectId}/segments`),
          api.get(`/projects/${projectId}/stakeholders?page_size=10000`),
          api.get(`/projects/${projectId}/stakeholder-selections`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/questions`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/responses?include_stakeholder_ids=true`).catch(() => ({ data: [] })),
          api.get(`/projects/${projectId}/dashboard-config`).catch(() => ({ data: null })),
          api.get(`/projects/${projectId}/user-permissions`).catch(() => ({ data: { allowed_segments: null } }))
        ]);
        const allowedSegs = permsRes.data?.allowed_segments;
        const allowedSet = allowedSegs ? new Set(allowedSegs) : null;
        setSegmentTypes(tRes.data);
        // Filter segments by permissions
        const allSegs = sRes.data;
        setSegments(allowedSet ? allSegs.filter(s => allowedSet.has(s.id)) : allSegs);
        const shs = shRes.data.stakeholders || shRes.data;
        // Filter stakeholders by permitted segments
        if (allowedSet) {
          setStakeholders(shs.filter(s => (s.segment_assignments || []).some(sa => allowedSet.has(sa.segment_id))));
        } else {
          setStakeholders(shs);
        }
        setSelections(selRes.data || []);
        setQuestions((qRes.data || []).filter(q => q.question_type === 'slider'));
        setResponses(rRes.data.responses || rRes.data || []);
        if (cfgRes.data?.anonymity_threshold) {
          setAnonymityThreshold(cfgRes.data.anonymity_threshold);
        }
        if (user?.email) {
          setCurrentSH(shs.find(s => s.email?.toLowerCase() === user.email.toLowerCase()) || null);
        }
        if (tRes.data.length > 0) {
          setTypeId1(tRes.data[0].id);
          if (tRes.data.length > 1) setTypeId2(tRes.data[1].id);
          if (tRes.data.length > 2) setTypeId3(tRes.data[2].id);
        }
      } catch (e) {
        console.error('Galaxy data fetch failed:', e);
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId, user]);

  /* ─── calculate segment scores when color-by question changes ─── */
  useEffect(() => {
    const safeResponses = Array.isArray(responses) ? responses : [];
    if (!colorByQuestion || !segments.length || !safeResponses.length) {
      setSegmentScores({});
      return;
    }
    const scores = {};
    segments.forEach(seg => {
      const segSHs = stakeholders.filter(s =>
        (s.segment_assignments || []).some(sa => sa.segment_id === seg.id)
      );
      const segIds = new Set(segSHs.map(s => s.id));
      const segR = safeResponses.filter(r =>
        r.question_id === colorByQuestion && segIds.has(r.stakeholder_id)
      );
      const vals = segR.map(r => {
        const v = parseFloat(r.answer) || parseFloat(r.value);
        return isNaN(v) ? null : v;
      }).filter(v => v !== null);
      scores[seg.id] = {
        responseCount: vals.length,
        avg: vals.length > 0 ? vals.reduce((a, b) => a + b, 0) / vals.length : null,
        meetsThreshold: vals.length >= anonymityThreshold
      };
    });
    setSegmentScores(scores);
  }, [colorByQuestion, segments, stakeholders, responses, anonymityThreshold]);

  /* ─── connection counts (both directions, using stakeholder IDs) ─── */
  const connCounts = useMemo(() => {
    const c = {};
    selections.forEach(s => {
      const fromId = s.user_stakeholder_id || s.user_id;
      c[fromId] = (c[fromId] || 0) + 1;
      c[s.target_id] = (c[s.target_id] || 0) + 1;
    });
    return c;
  }, [selections]);

  const maxConn = useMemo(
    () => Math.max(1, ...Object.values(connCounts).concat(1)),
    [connCounts]
  );

  /* ─── symmetric connection map ─── */
  const connMap = useMemo(() => {
    const m = new Map();
    selections.forEach(s => {
      const fromId = s.user_stakeholder_id || s.user_id;
      const k1 = `${fromId}_${s.target_id}`;
      const k2 = `${s.target_id}_${fromId}`;
      m.set(k1, (m.get(k1) || 0) + 1);
      m.set(k2, (m.get(k2) || 0) + 1);
    });
    return m;
  }, [selections]);

  const userConns = useMemo(() => {
    if (!currentSH) return new Set();
    const ids = new Set();
    selections.forEach(s => {
      const fromId = s.user_stakeholder_id || s.user_id;
      if (fromId === currentSH.id) ids.add(s.target_id);
      if (s.target_id === currentSH.id) ids.add(fromId);
    });
    return ids;
  }, [currentSH, selections]);

  /* ─── pairwise connection count ─── */
  const pairCount = useCallback((n1, n2) => {
    const ids = n => {
      if (n.type === 'individual' || n.type === 'ind-nested') return [n.shId || n.id];
      return (n.members || []).map(m => m.id);
    };
    const a = ids(n1), b = ids(n2);
    let total = 0;
    for (const i of a) for (const j of b) {
      if (i !== j) total += connMap.get(`${i}_${j}`) || 0;
    }
    return total;
  }, [connMap]);

  /* ═══ INDIVIDUAL GRAPH ═══ */
  const buildIndividual = useCallback(() => {
    const maxR = Math.min(dims.w, dims.h) / 2;
    const nodes = [];

    if (currentSH) {
      nodes.push({
        id: currentSH.id, shId: currentSH.id,
        name: `${currentSH.first_name || ''} ${currentSH.last_name || ''}`.trim(),
        type: 'individual', isCurrent: true, isConn: false,
        connCount: connCounts[currentSH.id] || 0,
        color: '#5AA2AE', nodeRadius: NODE_R + 2,
        x: 0, y: 0, fx: 0, fy: 0
      });
    }

    const others = stakeholders
      .filter(s => s.id !== currentSH?.id)
      .map(s => ({ ...s, _cc: connCounts[s.id] || 0 }))
      .sort((a, b) => b._cc - a._cc);

    others.forEach((sh, i) => {
      const norm = sh._cc / maxConn;
      const isConn = userConns.has(sh.id);
      const angle = i * GOLDEN;
      const r = 25 + (1 - norm) * (maxR - 35);
      nodes.push({
        id: sh.id, shId: sh.id,
        name: `${sh.first_name || ''} ${sh.last_name || ''}`.trim() || sh.email,
        type: 'individual', isCurrent: false, isConn,
        connCount: sh._cc,
        color: isConn ? '#6366f1' : (sh._cc > 0 ? '#94a3b8' : '#cbd5e1'),
        nodeRadius: NODE_R,
        x: Math.cos(angle) * r,
        y: Math.sin(angle) * r
      });
    });
    return { nodes, links: [] };
  }, [stakeholders, currentSH, userConns, connCounts, maxConn, dims]);

  /* ═══ HELPER: add children for deeply nested segments ═══ */
  const addSubChildren = useCallback((nodes, parentId, members, px, py, pR, pIdx, expandType) => {
    const inner = pR * 0.82;

    if (expandType === 'individuals' || !expandType) {
      // Block individual view if below anonymity threshold
      if (members.length < anonymityThreshold) return;
      const pos = spiralPositions(members.length, px, py, inner);
      members.forEach((sh, i) => {
        nodes.push({
          id: `${parentId}_${sh.id}`, shId: sh.id,
          name: `${sh.first_name || ''} ${sh.last_name || ''}`.trim(),
          type: 'ind-nested', parentId,
          isCurrent: sh.id === currentSH?.id, isConn: userConns.has(sh.id),
          color: sh.id === currentSH?.id ? '#5AA2AE' : '#64748b',
          nodeRadius: NODE_R - 1,
          x: pos[i].x, y: pos[i].y, fx: pos[i].x, fy: pos[i].y
        });
      });
      return;
    }

    const subSegs = segments.filter(s => s.segment_type_id === expandType);
    const validSubs = subSegs.filter(sub =>
      members.some(m => (m.segment_assignments || []).some(sa => sa.segment_id === sub.id))
    );

    const subData = validSubs.map((sub, i) => {
      const subMembers = members.filter(m =>
        (m.segment_assignments || []).some(sa => sa.segment_id === sub.id)
      );
      const subKey = `${parentId}_${sub.id}`;
      const exp = expanded.has(subKey);
      const r = exp
        ? groupRadius(subMembers.length, NODE_R - 1, true)
        : groupRadius(subMembers.length, NODE_R - 1, false);
      return { sub, subMembers, exp, r, subKey };
    });

    const subPos = circularPositions(
      subData.map(d => ({ r: d.r })), px, py, inner * 0.5
    );

    subData.forEach((d, i) => {
      const { sub, subMembers, exp, r, subKey } = d;
      const lx = subPos[i].x, ly = subPos[i].y;

      // Apply color-by-question to L3 sub-segments
      let color = COLORS[(i + 5) % COLORS.length];
      let insufficientData = false;
      let score = null;

      if (colorByQuestion) {
        const scoreInfo = segmentScores[sub.id];
        if (scoreInfo && scoreInfo.meetsThreshold && scoreInfo.avg !== null) {
          color = getScoreColor(scoreInfo.avg);
          score = scoreInfo.avg;
        } else {
          color = INSUFFICIENT_DATA_COLOR;
          insufficientData = true;
        }
      } else if (subMembers.length < anonymityThreshold) {
        color = INSUFFICIENT_DATA_COLOR;
        insufficientData = true;
      }

      nodes.push({
        id: subKey, name: sub.name, type: 'seg-l3',
        parentId, memberCount: subMembers.length, members: subMembers,
        color, score, insufficientData,
        isExpanded: exp, nodeSize: r,
        x: lx, y: ly, fx: lx, fy: ly
      });

      if (exp && !insufficientData) {
        const pos = spiralPositions(subMembers.length, lx, ly, r * 0.8);
        subMembers.forEach((sh, si) => {
          nodes.push({
            id: `${subKey}_${sh.id}`, shId: sh.id,
            name: `${sh.first_name || ''} ${sh.last_name || ''}`.trim(),
            type: 'ind-nested', parentId: subKey,
            isCurrent: sh.id === currentSH?.id, isConn: userConns.has(sh.id),
            color: sh.id === currentSH?.id ? '#5AA2AE' : '#64748b',
            nodeRadius: NODE_R - 2,
            x: pos[si].x, y: pos[si].y, fx: pos[si].x, fy: pos[si].y
          });
        });
      }
    });
  }, [segments, expanded, currentSH, userConns,
      colorByQuestion, segmentScores, anonymityThreshold]);

  /* ═══ HELPER: add L1 segment children ═══ */
  const addL1Children = useCallback((nodes, parentSeg, members, px, py, pR, pIdx, expandType) => {
    const inner = pR * 0.82;

    if (expandType === 'individuals') {
      // Block individual view if below anonymity threshold
      const meetsThreshold = members.length >= anonymityThreshold;
      if (!meetsThreshold) return;
      const pos = spiralPositions(members.length, px, py, inner);
      members.forEach((sh, i) => {
        nodes.push({
          id: `${parentSeg.id}_${sh.id}`, shId: sh.id,
          name: `${sh.first_name || ''} ${sh.last_name || ''}`.trim(),
          type: 'ind-nested', parentId: parentSeg.id,
          isCurrent: sh.id === currentSH?.id, isConn: userConns.has(sh.id),
          color: sh.id === currentSH?.id ? '#5AA2AE' : COLORS_LIGHT[pIdx % COLORS_LIGHT.length],
          nodeRadius: NODE_R,
          x: pos[i].x, y: pos[i].y, fx: pos[i].x, fy: pos[i].y
        });
      });
      return;
    }

    if (!expandType) return;
    const subSegs = segments.filter(s => s.segment_type_id === expandType);
    const validSubs = subSegs.filter(sub =>
      members.some(m => (m.segment_assignments || []).some(sa => sa.segment_id === sub.id))
    );

    const subData = validSubs.map((sub, i) => {
      const subMembers = members.filter(m =>
        (m.segment_assignments || []).some(sa => sa.segment_id === sub.id)
      );
      const subKey = `${parentSeg.id}_${sub.id}`;
      const exp = expanded.has(subKey);
      const subExpandType = expanded.get(subKey);
      const r = exp
        ? groupRadius(subMembers.length, NODE_R, true)
        : groupRadius(subMembers.length, NODE_R, false);
      return { sub, subMembers, exp, subExpandType, r, subKey };
    });

    const subPos = circularPositions(
      subData.map(d => ({ r: d.r })), px, py, inner * 0.5
    );

    subData.forEach((d, i) => {
      const { sub, subMembers, exp, subExpandType, r, subKey } = d;
      const lx = subPos[i].x, ly = subPos[i].y;

      // Apply color-by-question to sub-segments
      let color = COLORS_LIGHT[i % COLORS_LIGHT.length];
      let insufficientData = false;
      let score = null;

      if (colorByQuestion) {
        const scoreInfo = segmentScores[sub.id];
        if (scoreInfo && scoreInfo.meetsThreshold && scoreInfo.avg !== null) {
          color = getScoreColor(scoreInfo.avg);
          score = scoreInfo.avg;
        } else {
          color = INSUFFICIENT_DATA_COLOR;
          insufficientData = true;
        }
      } else if (subMembers.length < anonymityThreshold) {
        color = INSUFFICIENT_DATA_COLOR;
        insufficientData = true;
      }

      nodes.push({
        id: subKey, name: sub.name, type: 'seg-l2',
        parentId: parentSeg.id, memberCount: subMembers.length, members: subMembers,
        color, score, insufficientData,
        isExpanded: exp, nodeSize: r,
        x: lx, y: ly, fx: lx, fy: ly
      });

      if (exp && !insufficientData) addSubChildren(nodes, subKey, subMembers, lx, ly, r, i, subExpandType);
    });
  }, [segments, expanded, currentSH, userConns, addSubChildren,
      colorByQuestion, segmentScores, anonymityThreshold]);

  /* ═══ GROUPED GRAPH ═══ */
  const buildGrouped = useCallback(() => {
    if (!typeId1) return { nodes: [], links: [] };
    const nodes = [], links = [];

    const l1Segs = segments.filter(s => s.segment_type_id === typeId1);
    const validL1 = l1Segs.filter(seg =>
      stakeholders.some(sh => (sh.segment_assignments || []).some(sa => sa.segment_id === seg.id))
    );

    const l1Data = validL1.map((seg, idx) => {
      const members = stakeholders.filter(sh =>
        (sh.segment_assignments || []).some(sa => sa.segment_id === seg.id)
      );
      const exp = expanded.has(seg.id);
      const expandType = expanded.get(seg.id); // 'individuals' or a segment_type_id
      let radius;

      if (!exp) {
        radius = groupRadius(members.length, NODE_R, false);
      } else if (expandType === 'individuals') {
        radius = groupRadius(members.length, NODE_R + 1, true);
      } else if (expandType) {
        // Expanding by a sub-segment type
        const subSegs = segments.filter(s => s.segment_type_id === expandType);
        const validSubs = subSegs.filter(sub =>
          members.some(m => (m.segment_assignments || []).some(sa => sa.segment_id === sub.id))
        );
        const subRadii = validSubs.map(sub => {
          const subMembers = members.filter(m =>
            (m.segment_assignments || []).some(sa => sa.segment_id === sub.id)
          );
          const subKey = `${seg.id}_${sub.id}`;
          const subExp = expanded.has(subKey);
          return subExp
            ? groupRadius(subMembers.length, NODE_R, true)
            : groupRadius(subMembers.length, NODE_R, false);
        });
        const maxSubR = Math.max(18, ...subRadii);
        radius = groupRadius(validSubs.length, maxSubR, true);
      } else {
        radius = groupRadius(members.length, NODE_R + 1, true);
      }

      return { seg, members, exp, expandType, radius, idx };
    });

    const l1Pos = circularPositions(
      l1Data.map(d => ({ r: d.radius })), 0, 0, 100
    );

    l1Data.forEach((d, i) => {
      const { seg, members, exp, expandType, radius, idx } = d;
      const px = l1Pos[i].x, py = l1Pos[i].y;

      // Determine color: color-by-question or default
      let color = COLORS[idx % COLORS.length];
      let insufficientData = false;
      let score = null;
      const meetsAnonymity = members.length >= anonymityThreshold;

      if (colorByQuestion) {
        const scoreInfo = segmentScores[seg.id];
        if (scoreInfo && scoreInfo.meetsThreshold && scoreInfo.avg !== null) {
          color = getScoreColor(scoreInfo.avg);
          score = scoreInfo.avg;
        } else {
          color = INSUFFICIENT_DATA_COLOR;
          insufficientData = true;
        }
      } else if (!meetsAnonymity) {
        color = INSUFFICIENT_DATA_COLOR;
        insufficientData = true;
      }

      nodes.push({
        id: seg.id, name: seg.name, type: 'seg-l1',
        memberCount: members.length, members,
        color, score, insufficientData,
        isExpanded: exp, nodeSize: radius,
        x: px, y: py, fx: px, fy: py
      });

      if (exp && !insufficientData) addL1Children(nodes, seg, members, px, py, radius, idx, expandType);
    });

    // Sort nodes so parent segments draw first (below), children on top
    // Canvas renders in array order - first items drawn first (behind later items)
    const typeOrder = { 'seg-l1': 0, 'seg-l2': 1, 'seg-l3': 2, 'ind-nested': 3 };
    nodes.sort((a, b) => (typeOrder[a.type] || 0) - (typeOrder[b.type] || 0));

    return { nodes, links: [] };
  }, [segments, stakeholders, typeId1, expanded, pairCount, addL1Children,
      colorByQuestion, segmentScores, anonymityThreshold]);

  /* ═══ GRAPH DISPATCHER ═══ */
  const buildGraph = useCallback(() => {
    if (stakeholders.length === 0) return { nodes: [], links: [] };
    return mode === MODES.INDIVIDUAL ? buildIndividual() : buildGrouped();
  }, [stakeholders, mode, buildIndividual, buildGrouped]);

  /* ─── animate transition ─── */
  const animateTransition = useCallback((oldNodes, newGraph) => {
    if (animRef.current) cancelAnimationFrame(animRef.current);
    setAnimating(true);

    const oldMap = new Map(oldNodes.map(n => [n.id, n]));
    let step = 0;

    const tick = () => {
      step++;
      const t = Math.min(1, step / ANIM_STEPS);
      const e = easeInOutQuad(t);

      const animated = newGraph.nodes.map(n => {
        const old = oldMap.get(n.id);
        const ox = old ? old.x : (n.parentId ? (oldMap.get(n.parentId)?.x ?? n.x) : n.x);
        const oy = old ? old.y : (n.parentId ? (oldMap.get(n.parentId)?.y ?? n.y) : n.y);
        return {
          ...n,
          x: ox + (n.x - ox) * e,
          y: oy + (n.y - oy) * e,
          fx: n.fx !== undefined ? (ox + (n.fx - ox) * e) : undefined,
          fy: n.fy !== undefined ? (oy + (n.fy - oy) * e) : undefined,
        };
      });

      setGraphData({ nodes: animated, links: newGraph.links });

      if (t < 1) {
        animRef.current = requestAnimationFrame(tick);
      } else {
        setAnimating(false);
        setGraphData(newGraph);
      }
    };
    animRef.current = requestAnimationFrame(tick);
  }, []);

  /* ─── rebuild on deps change ─── */
  useEffect(() => {
    if (stakeholders.length === 0) return;
    // Wait for segmentScores to be populated before rebuilding with color-by
    if (colorByQuestion && Object.keys(segmentScores).length === 0) return;
    const oldNodes = graphData.nodes;
    const newGraph = buildGraph();

    if (oldNodes.length > 0 && !animating) {
      animateTransition(oldNodes, newGraph);
    } else if (!animating) {
      setGraphData(newGraph);
    }
    forcesConfigured.current = false;
  }, [stakeholders, segments, typeId1,
      expanded, dims, userConns, mode, buildGraph,
      colorByQuestion, segmentScores]);

  /* ─── configure d3 forces ─── */
  useEffect(() => {
    const fg = graphRef.current;
    if (!fg || graphData.nodes.length === 0 || forcesConfigured.current) return;

    const timer = setTimeout(() => {
      try {
        if (mode === MODES.INDIVIDUAL) {
          const maxR = Math.min(dims.w, dims.h) / 2;
          fg.d3Force('center', null);

          fg.d3Force('radial', forceRadial(node => {
            if (node.fx !== undefined && node.fy !== undefined) return 0;
            const norm = (node.connCount || 0) / maxConn;
            return 25 + (1 - norm) * (maxR - 35);
          }, 0, 0).strength(0.5));

          fg.d3Force('collide', forceCollide(node =>
            (node.nodeRadius || NODE_R) + 1.5
          ).strength(0.9).iterations(3));

          const charge = fg.d3Force('charge');
          if (charge) charge.strength(-3);

          fg.d3ReheatSimulation();
        } else {
          fg.d3Force('center', null);
          fg.d3Force('radial', null);
          const charge = fg.d3Force('charge');
          if (charge) charge.strength(0);
          fg.d3Force('collide', forceCollide(node => {
            if (node.type?.startsWith('seg')) return (node.nodeSize || 20) + 2;
            return (node.nodeRadius || NODE_R) + 1;
          }).strength(1));
        }
        forcesConfigured.current = true;
      } catch (e) {
        console.warn('Force config error:', e);
      }
    }, 50);

    return () => clearTimeout(timer);
  }, [mode, graphData.nodes.length, maxConn, dims]);

  /* ─── selected node connections (precomputed) ─── */
  const selectedConns = useMemo(() => {
    if (!selectedId || graphData.nodes.length === 0) return [];
    const src = graphData.nodes.find(n => n.id === selectedId);
    if (!src) return [];

    // Build parent chain for nested individuals (child→parent exclusion)
    const isNested = src.type === 'ind-nested';
    const srcParentChain = new Set();
    if (isNested) {
      let pid = src.parentId;
      while (pid) {
        srcParentChain.add(pid);
        const parentNode = graphData.nodes.find(n => n.id === pid);
        pid = parentNode?.parentId || null;
      }
    }

    // Build child set for segments (parent→child exclusion)
    const isSeg = src.type?.startsWith('seg');
    const childIds = new Set();
    if (isSeg) {
      graphData.nodes.forEach(n => {
        // Direct children
        if (n.parentId === src.id) childIds.add(n.id);
        // Grandchildren (children of children)
        if (n.parentId) {
          const parent = graphData.nodes.find(p => p.id === n.parentId);
          if (parent?.parentId === src.id) childIds.add(n.id);
        }
      });
    }

    let mx = 1;
    const lines = [];
    graphData.nodes.forEach(tgt => {
      if (tgt.id === selectedId) return;

      // Skip ancestor segments when source is nested individual
      if (isNested && tgt.type?.startsWith('seg') && srcParentChain.has(tgt.id)) return;
      // Skip child/descendant nodes when source is a segment
      if (isSeg && childIds.has(tgt.id)) return;

      const c = pairCount(src, tgt);
      if (c > 0) {
        lines.push({ tid: tgt.id, c });
        if (c > mx) mx = c;
      }
    });
    lines.forEach(l => { l.norm = l.c / mx; });
    return lines;
  }, [selectedId, graphData.nodes, pairCount]);

  // Set of connected node IDs (for z-ordering in paint)
  const connectedSet = useMemo(() => {
    if (!selectedId || selectedConns.length === 0) return new Set();
    const s = new Set([selectedId]);
    selectedConns.forEach(c => s.add(c.tid));
    return s;
  }, [selectedId, selectedConns]);

  /* ─── post-render: draw lines from circle edges THEN repaint connected nodes on top ─── */
  const onFramePost = useCallback((ctx) => {
    if (selectedConns.length === 0) return;

    const srcData = nodeDataRef.current.get(selectedId);
    const srcPos = posRef.current.get(selectedId);
    if (!srcPos || srcPos.x == null || !srcData) return;
    const srcR = srcData.type?.startsWith('seg') ? (srcData.nodeSize || 20) : (srcData.nodeRadius || NODE_R);

    // 1. Draw connection lines from circle edges
    selectedConns.forEach(({ tid, c, norm }) => {
      const tgtPos = posRef.current.get(tid);
      const tgtData = nodeDataRef.current.get(tid);
      if (!tgtPos || tgtPos.x == null || !tgtData) return;
      const tgtR = tgtData.type?.startsWith('seg') ? (tgtData.nodeSize || 20) : (tgtData.nodeRadius || NODE_R);

      // Calculate angle and edge offsets
      const dx = tgtPos.x - srcPos.x;
      const dy = tgtPos.y - srcPos.y;
      const dist = Math.sqrt(dx * dx + dy * dy);
      if (dist < 0.1) return;
      const nx = dx / dist, ny = dy / dist;

      const x1 = srcPos.x + nx * (srcR + 1);
      const y1 = srcPos.y + ny * (srcR + 1);
      const x2 = tgtPos.x - nx * (tgtR + 1);
      const y2 = tgtPos.y - ny * (tgtR + 1);

      const thick = 1 + norm * 2;
      const alpha = 0.45 + norm * 0.4;

      ctx.beginPath();
      ctx.moveTo(x1, y1);
      ctx.lineTo(x2, y2);
      ctx.strokeStyle = `rgba(245,158,11,${alpha})`;
      ctx.lineWidth = thick;
      ctx.stroke();

      // Small count badge at midpoint
      if (c > 1) {
        const mx = (x1 + x2) / 2, my = (y1 + y2) / 2;
        ctx.font = '4.5px Inter,sans-serif';
        const tw = ctx.measureText(String(c)).width;
        ctx.beginPath();
        ctx.arc(mx, my, tw / 2 + 2.5, 0, Math.PI * 2);
        ctx.fillStyle = 'rgba(245,158,11,0.9)';
        ctx.fill();
        ctx.fillStyle = '#fff';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText(String(c), mx, my);
      }
    });

    // 2. Repaint connected + selected nodes ON TOP of lines
    const repaintNode = (nid) => {
      const n = nodeDataRef.current.get(nid);
      if (!n || n.x == null) return;
      const hov = hoveredId === n.id;
      const sel = selectedId === n.id;
      if (n.type === 'individual' || n.type === 'ind-nested') {
        drawPerson(ctx, n, hov, sel);
      } else {
        drawSegment(ctx, n, hov, sel);
      }
    };

    // Repaint connected targets first, then selected node on top
    selectedConns.forEach(({ tid }) => repaintNode(tid));
    repaintNode(selectedId);
  }, [selectedId, selectedConns, hoveredId]);

  /* ═══ PAINT FUNCTIONS (pure drawing, no state deps) ═══ */
  const drawPerson = useCallback((ctx, n, hov, sel) => {
    const { x, y, isCurrent, isConn, nodeRadius } = n;
    const r = nodeRadius || NODE_R;

    if (sel) {
      ctx.beginPath();
      ctx.arc(x, y, r + 3.5, 0, Math.PI * 2);
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2;
      ctx.stroke();
    }
    if (hov && !sel) {
      ctx.beginPath();
      ctx.arc(x, y, r + 2, 0, Math.PI * 2);
      ctx.fillStyle = isCurrent ? 'rgba(90,162,174,0.2)' : (isConn ? 'rgba(99,102,241,0.2)' : 'rgba(148,163,184,0.15)');
      ctx.fill();
    }

    // Node circle
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = isCurrent ? '#5AA2AE' : (isConn ? '#6366f1' : '#e2e8f0');
    ctx.fill();
    ctx.strokeStyle = isCurrent ? '#3d8a94' : (isConn ? '#4f46e5' : '#cbd5e1');
    ctx.lineWidth = 0.8;
    ctx.stroke();

    // Person icon (centered)
    if (r >= 3.5) {
      ctx.save();
      const s = r / 12;
      ctx.translate(x, y);
      ctx.scale(s, s);
      ctx.translate(-12, -12);
      ctx.fillStyle = isCurrent || isConn ? '#fff' : '#64748b';
      ctx.fill(PERSON_PATH);
      ctx.restore();
    }

    // Current user ring
    if (isCurrent) {
      ctx.beginPath();
      ctx.arc(x, y, r + 1.5, 0, Math.PI * 2);
      ctx.strokeStyle = '#5AA2AE';
      ctx.lineWidth = 1;
      ctx.setLineDash([2, 2]);
      ctx.stroke();
      ctx.setLineDash([]);
    }
  }, []);

  const drawSegment = useCallback((ctx, n, hov, sel) => {
    const { x, y, name, memberCount, color, isExpanded, nodeSize, score, insufficientData } = n;
    const r = nodeSize || 20;

    if (sel) {
      ctx.beginPath();
      ctx.arc(x, y, r + 4, 0, Math.PI * 2);
      ctx.strokeStyle = '#f59e0b';
      ctx.lineWidth = 2.5;
      ctx.stroke();
    }
    if (hov && !sel) {
      ctx.beginPath();
      ctx.arc(x, y, r + 3, 0, Math.PI * 2);
      ctx.fillStyle = withAlpha(color, 0.1);
      ctx.fill();
    }

    if (isExpanded) {
      ctx.beginPath();
      ctx.arc(x, y, r + 2, 0, Math.PI * 2);
      ctx.strokeStyle = color;
      ctx.lineWidth = 1.5;
      ctx.setLineDash([3, 2]);
      ctx.stroke();
      ctx.setLineDash([]);
    }

    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fillStyle = isExpanded ? withAlpha(color, 0.19) : color;
    ctx.fill();
    ctx.strokeStyle = isExpanded ? color : '#ffffffcc';
    ctx.lineWidth = 1;
    ctx.stroke();

    if (r >= 12) {
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      const fs = Math.max(5, Math.min(9, r / 4));
      const mw = r * 1.4;
      const textColor = isExpanded ? color : (insufficientData ? '#64748b' : '#fff');
      const countText = insufficientData ? `<${anonymityThreshold}` : String(memberCount);

      // Wrap label to max 2 lines
      ctx.font = `600 ${fs}px Inter,system-ui,sans-serif`;
      const words = (name || '').split(/\s+/);
      let lines;
      if (ctx.measureText(name).width > mw && words.length > 1) {
        let line1 = words[0], breakIdx = 1;
        for (let w = 1; w < words.length; w++) {
          const test = line1 + ' ' + words[w];
          if (ctx.measureText(test).width <= mw) { line1 = test; breakIdx = w + 1; }
          else break;
        }
        let line2 = words.slice(breakIdx).join(' ');
        if (line2) {
          while (ctx.measureText(line2).width > mw && line2.length > 3) line2 = line2.slice(0, -3) + '..';
          lines = [line1, line2];
        } else {
          lines = [line1];
        }
      } else {
        let label = name || '';
        while (ctx.measureText(label).width > mw && label.length > 3) label = label.slice(0, -3) + '..';
        lines = [label];
      }

      const lineH = fs * 1.15;

      if (score !== null && score !== undefined) {
        // Name lines + score + count
        const totalH = lines.length * lineH + lineH + fs * 0.7;
        const startY = y - totalH / 2 + lineH / 2;
        ctx.fillStyle = textColor;
        ctx.font = `600 ${fs}px Inter,system-ui,sans-serif`;
        lines.forEach((ln, li) => ctx.fillText(ln, x, startY + li * lineH));
        ctx.font = `600 ${fs}px Inter,system-ui,sans-serif`;
        ctx.fillText(score.toFixed(1), x, startY + lines.length * lineH);
        ctx.font = `400 ${fs * 0.7}px Inter,system-ui,sans-serif`;
        ctx.fillStyle = isExpanded ? `${color}aa` : '#ffffffaa';
        ctx.fillText(`(${countText})`, x, startY + lines.length * lineH + fs * 0.85);
      } else {
        // Name lines + count
        const totalH = lines.length * lineH + fs * 0.85;
        const startY = y - totalH / 2 + lineH / 2;
        ctx.fillStyle = textColor;
        ctx.font = `600 ${fs}px Inter,system-ui,sans-serif`;
        lines.forEach((ln, li) => ctx.fillText(ln, x, startY + li * lineH));
        ctx.font = `400 ${fs * 0.75}px Inter,system-ui,sans-serif`;
        ctx.fillText(countText, x, startY + lines.length * lineH);
      }
    }
  }, [anonymityThreshold]);

  /* ─── paintNode: store data + draw (skip connected nodes when lines showing) ─── */
  const paintNode = useCallback((node, ctx) => {
    if (node.x == null || node.y == null) return;

    // Always store live position + data for onFramePost repainting
    posRef.current.set(node.id, { x: node.x, y: node.y });
    nodeDataRef.current.set(node.id, node);

    const hov = hoveredId === node.id;
    const sel = selectedId === node.id;

    // If connections are showing and this node is connected/selected,
    // it will be repainted in onFramePost ON TOP of lines. Still paint it
    // here so it appears even before onFramePost runs, but onFramePost
    // will paint it again on top.
    if (node.type === 'individual' || node.type === 'ind-nested') {
      drawPerson(ctx, node, hov, sel);
    } else {
      drawSegment(ctx, node, hov, sel);
    }
  }, [hoveredId, selectedId, drawPerson, drawSegment]);

  const pointerArea = useCallback((node, color, ctx) => {
    if (node.x == null) return;
    const r = node.type?.startsWith('seg')
      ? (node.nodeSize || 20) + 4
      : (node.nodeRadius || NODE_R) + 3;
    ctx.beginPath();
    ctx.arc(node.x, node.y, r, 0, Math.PI * 2);
    ctx.fillStyle = color;
    ctx.fill();
  }, []);

  const paintLink = useCallback((link, ctx) => {
    if (!link.source?.x || !link.target?.x) return;
    ctx.beginPath();
    ctx.moveTo(link.source.x, link.source.y);
    ctx.lineTo(link.target.x, link.target.y);
    ctx.strokeStyle = '#e2e8f044';
    ctx.lineWidth = 0.8;
    ctx.setLineDash([3, 3]);
    ctx.stroke();
    ctx.setLineDash([]);
  }, []);

  /* ─── event handlers ─── */
  // Click: select individual node OR show drill-down popup for segments
  const handleClick = useCallback((node, event) => {
    setDrillDownMenu(null); // close any open menu

    // Individual nodes: toggle selection to show connections
    if (node.type === 'individual' || node.type === 'ind-nested') {
      setSelectedId(prev => prev === node.id ? null : node.id);
      return;
    }

    // Segment nodes below anonymity threshold: block all interaction
    if (node.type?.startsWith('seg') && node.insufficientData) {
      return;
    }

    // Segment nodes: if already expanded, collapse
    if (node.type?.startsWith('seg') && node.isExpanded) {
      setExpanded(prev => {
        const newMap = new Map(prev);
        // Remove this node and all children that start with this id
        for (const key of prev.keys()) {
          if (key === node.id || key.startsWith(node.id + '_')) {
            newMap.delete(key);
          }
        }
        return newMap;
      });
      return;
    }

    // Segment nodes: not expanded — show drill-down popup
    if (node.type?.startsWith('seg')) {
      setSelectedId(node.id);
      setDrillDownMenu({
        x: event?.clientX ?? 400,
        y: event?.clientY ?? 300,
        node
      });
    }
  }, []);

  // Handle drill-down selection from popup
  const handleDrillDown = useCallback((expandType) => {
    if (!drillDownMenu) return;
    setExpanded(prev => {
      const newMap = new Map(prev);
      newMap.set(drillDownMenu.node.id, expandType);
      return newMap;
    });
    setDrillDownMenu(null);
  }, [drillDownMenu]);

  // Close drill-down on outside click
  useEffect(() => {
    if (!drillDownMenu) return;
    const close = () => setDrillDownMenu(null);
    const timer = setTimeout(() => window.addEventListener('click', close), 50);
    return () => { clearTimeout(timer); window.removeEventListener('click', close); };
  }, [drillDownMenu]);

  // Available segment types for drill-down (exclude the type already used at this level)
  const availableDrillTypes = useMemo(() => {
    if (!drillDownMenu) return [];
    // Collect all segment type IDs already used in the expansion chain
    const usedTypes = new Set([typeId1]);
    for (const val of expanded.values()) {
      if (val !== 'individuals') usedTypes.add(val);
    }
    return segmentTypes.filter(st => !usedTypes.has(st.id));
  }, [drillDownMenu, segmentTypes, typeId1, expanded]);

  const handleModeChange = useCallback((m) => {
    setMode(m);
    setExpanded(new Map());
    setSelectedId(null);
    setDrillDownMenu(null);
    forcesConfigured.current = false;
    initialZoomDone.current = false;
  }, []);

  /* ─── center & zoom ─── */
  const handleEngineStop = useCallback(() => {
    if (!initialZoomDone.current && graphRef.current && graphData.nodes.length > 0) {
      graphRef.current.zoomToFit(400, 40);
      initialZoomDone.current = true;
    }
  }, [graphData.nodes.length]);

  useEffect(() => { initialZoomDone.current = false; }, [mode, expanded.size]);

  // Zoom to fit after grouped view expand/collapse
  useEffect(() => {
    if (mode === MODES.INDIVIDUAL || !graphRef.current || graphData.nodes.length === 0) return;
    const t = setTimeout(() => {
      graphRef.current?.zoomToFit(400, 40);
    }, ANIM_STEPS * 17 + 150);
    return () => clearTimeout(t);
  }, [expanded.size, mode, graphData.nodes.length]);

  useEffect(() => {
    return () => { if (animRef.current) cancelAnimationFrame(animRef.current); };
  }, []);

  const zoomIn = () => graphRef.current?.zoom(graphRef.current.zoom() * 1.3, 200);
  const zoomOut = () => graphRef.current?.zoom(graphRef.current.zoom() / 1.3, 200);
  const resetView = () => {
    graphRef.current?.zoomToFit(300, 40);
    setExpanded(new Map());
    setSelectedId(null);
    setDrillDownMenu(null);
  };

  /* ═══ RENDER ═══ */
  if (loading) {
    return (
      <div className="p-6 space-y-6">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-[600px] w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 md:p-6 space-y-4" data-testid="people-galaxy-2-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Layers className="w-6 h-6 text-[#5AA2AE]" />
            People Galaxy
            <Badge variant="secondary" className="ml-2">Beta</Badge>
          </h1>
          <p className="text-sm text-slate-500">Click to select, click groups to expand</p>
        </div>

        <div className="flex items-center gap-4 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="text-sm text-slate-500">Grouping:</span>
            <Select value={mode} onValueChange={handleModeChange} disabled={animating}>
              <SelectTrigger className="w-32" data-testid="grouping-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={MODES.INDIVIDUAL}>
                  <span className="flex items-center gap-2"><User className="w-4 h-4" />Individual</span>
                </SelectItem>
                <SelectItem value={MODES.LEVEL_1}>
                  <span className="flex items-center gap-2"><Users className="w-4 h-4" />Grouped</span>
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          {mode !== MODES.INDIVIDUAL && (
            <>
              <div className="flex items-center gap-2">
                <span className="text-sm text-slate-500">Group by:</span>
                <Select value={typeId1 || ''} onValueChange={setTypeId1}>
                  <SelectTrigger className="w-36" data-testid="type1-select"><SelectValue placeholder="Segment type" /></SelectTrigger>
                  <SelectContent>
                    {segmentTypes.map(st => <SelectItem key={st.id} value={st.id}>{st.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {mode !== MODES.INDIVIDUAL && (
            <div className="flex items-center gap-2">
              <Palette className="w-4 h-4 text-slate-400" />
              <span className="text-sm text-slate-500">Color by:</span>
              <Select value={colorByQuestion || 'none'} onValueChange={v => setColorByQuestion(v === 'none' ? null : v)}>
                <SelectTrigger className="w-48" data-testid="color-by-select">
                  <SelectValue placeholder="Default colors" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">Default colors</SelectItem>
                  {questions.map(q => (
                    <SelectItem key={q.id} value={q.id}>
                      {q.heading || q.text?.substring(0, 30)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="flex items-center gap-1 bg-slate-100 rounded-lg p-1">
            <Button variant="ghost" size="sm" onClick={zoomOut} className="h-8 w-8 p-0" data-testid="zoom-out-btn">
              <ZoomOut className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={zoomIn} className="h-8 w-8 p-0" data-testid="zoom-in-btn">
              <ZoomIn className="w-4 h-4" />
            </Button>
            <Button variant="ghost" size="sm" onClick={resetView} className="h-8 w-8 p-0" data-testid="reset-view-btn">
              <Maximize2 className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </div>

      {/* Status */}
      {currentSH && (
        <div className="flex items-center gap-3 p-3 bg-[#5AA2AE]/10 rounded-lg border border-[#5AA2AE]/20">
          <div className="w-8 h-8 rounded-full bg-[#5AA2AE] flex items-center justify-center">
            <User className="w-4 h-4 text-white" />
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">
              {currentSH.first_name} {currentSH.last_name}
            </p>
            <p className="text-xs text-slate-500">{userConns.size} connections</p>
          </div>
          {animating && <Badge className="bg-blue-100 text-blue-700 animate-pulse">Animating...</Badge>}
          {selectedId && <Badge className="bg-amber-100 text-amber-700">Showing connections</Badge>}
        </div>
      )}

      {/* Color Legend */}
      {colorByQuestion && mode !== MODES.INDIVIDUAL && (
        <div className="flex items-center gap-4 p-3 bg-gradient-to-r from-rose-50 via-amber-50 to-emerald-50 rounded-lg border" data-testid="color-legend">
          <span className="text-sm font-medium text-slate-700">
            {questions.find(q => q.id === colorByQuestion)?.heading || 'Score'}
          </span>
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full" style={{ background: getScoreColor(0) }} />
              <span className="text-xs text-slate-500">0</span>
            </div>
            <div className="w-24 h-3 rounded-full" style={{
              background: 'linear-gradient(to right, rgb(239, 68, 68), rgb(234, 179, 8), rgb(34, 197, 94))'
            }} />
            <div className="flex items-center gap-1">
              <div className="w-4 h-4 rounded-full" style={{ background: getScoreColor(10) }} />
              <span className="text-xs text-slate-500">10</span>
            </div>
          </div>
          <div className="flex items-center gap-1 ml-4">
            <div className="w-4 h-4 rounded-full bg-gray-400" />
            <span className="text-xs text-slate-500">&lt;{anonymityThreshold} responses</span>
          </div>
        </div>
      )}

      {/* Expanded */}
      {expanded.size > 0 && (
        <div className="flex items-center gap-2 p-3 bg-purple-50 rounded-lg flex-wrap">
          <Button variant="ghost" size="sm" onClick={() => setExpanded(new Map())} className="h-7 gap-1">
            <Minus className="w-3 h-3" />Collapse All
          </Button>
          {Array.from(expanded.keys()).map(id => {
            const n = graphData.nodes.find(nd => nd.id === id);
            return n ? <Badge key={id} variant="secondary" className="bg-purple-100">{n.name}</Badge> : null;
          })}
        </div>
      )}

      {/* Graph */}
      <Card className="overflow-hidden">
        <CardContent ref={containerRef} className="p-0 relative" style={{ height: dims.h }}>
          <ForceGraph2D
            ref={graphRef}
            graphData={graphData}
            nodeCanvasObject={paintNode}
            nodePointerAreaPaint={pointerArea}
            linkCanvasObject={paintLink}
            onRenderFramePost={onFramePost}
            nodeLabel={node => {
              if (node.type === 'individual' || node.type === 'ind-nested') {
                return node.isCurrent ? node.name : '';
              }
              if (node.insufficientData) {
                return `${node.name}: Group too small (< ${anonymityThreshold}) — details hidden`;
              }
              return '';
            }}
            nodeRelSize={NODE_R}
            d3AlphaDecay={0.06}
            d3VelocityDecay={0.4}
            cooldownTicks={60}
            warmupTicks={40}
            onNodeHover={n => setHoveredId(n?.id || null)}
            onNodeClick={handleClick}
            onEngineStop={handleEngineStop}
            enableNodeDrag={mode === MODES.INDIVIDUAL && !animating}
            enableZoomInteraction={true}
            enablePanInteraction={true}
            backgroundColor="#f8fafc"
            width={dims.w}
            height={dims.h}
          />

          <div className="absolute bottom-4 left-4 bg-white/95 backdrop-blur rounded-lg shadow-lg p-3 text-sm border">
            <div className="flex items-center gap-2 text-slate-600">
              <MousePointerClick className="w-4 h-4 text-[#5AA2AE]" />
              <span>Click nodes to select &middot; Click groups to expand</span>
            </div>
          </div>

          {/* Drill-down popup */}
          {drillDownMenu && (
            <div
              className="fixed bg-white rounded-xl shadow-2xl border-2 border-slate-200 p-1 z-[100] min-w-[220px]"
              style={{
                left: Math.min(drillDownMenu.x, window.innerWidth - 240),
                top: Math.min(drillDownMenu.y - 50, window.innerHeight - 250)
              }}
              onClick={e => e.stopPropagation()}
              data-testid="drill-down-menu"
            >
              <div className="px-4 py-3 border-b bg-slate-50 rounded-t-lg">
                <p className="text-sm font-semibold text-slate-700">
                  Expand: {drillDownMenu.node.name}
                </p>
                <p className="text-xs text-slate-500">
                  {drillDownMenu.node.insufficientData
                    ? `<${anonymityThreshold} members`
                    : `${drillDownMenu.node.memberCount} members`
                  }
                </p>
              </div>
              <div className="py-2">
                {availableDrillTypes.map(st => (
                  <button
                    key={st.id}
                    className="w-full px-4 py-2.5 text-sm text-left hover:bg-[#5AA2AE] hover:text-white rounded-lg flex items-center gap-3 transition-colors"
                    onClick={() => handleDrillDown(st.id)}
                    data-testid={`drill-by-${st.name.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    <ChevronRight className="w-4 h-4" />
                    <span>By {st.name}</span>
                  </button>
                ))}
                {drillDownMenu.node.memberCount >= anonymityThreshold && (
                <button
                  className="w-full px-4 py-2.5 text-sm text-left hover:bg-[#5AA2AE] hover:text-white rounded-lg flex items-center gap-3 transition-colors"
                  onClick={() => handleDrillDown('individuals')}
                  data-testid="drill-by-individuals"
                >
                  <User className="w-4 h-4" />
                  <span>Show Individuals</span>
                </button>
                )}
                {drillDownMenu.node.memberCount < anonymityThreshold && (
                  <div className="px-4 py-2.5 text-xs text-slate-400 flex items-center gap-2">
                    <Lock className="w-3.5 h-3.5" />
                    <span>&lt;{anonymityThreshold} responses — individuals hidden</span>
                  </div>
                )}
              </div>
              <div className="border-t px-4 py-2">
                <button
                  className="w-full text-sm text-slate-500 hover:text-slate-700 py-1"
                  onClick={() => setDrillDownMenu(null)}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Legend */}
      <div className="flex items-center gap-5 p-4 bg-slate-50 rounded-lg flex-wrap text-sm" data-testid="galaxy-legend">
        <div className="flex items-center gap-2">
          <div className="w-5 h-5 rounded-full bg-[#5AA2AE] flex items-center justify-center">
            <User className="w-3 h-3 text-white" />
          </div>
          <span className="text-slate-600">You</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full bg-[#6366f1]" />
          <span className="text-slate-600">Connected</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-3.5 h-3.5 rounded-full bg-slate-200" />
          <span className="text-slate-600">Other</span>
        </div>
        <div className="w-px h-5 bg-slate-300" />
        <div className="flex items-center gap-2">
          <div className="w-4 h-4 rounded-full border-2 border-amber-400" />
          <span className="text-slate-600">Selected</span>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-5 h-1 bg-gradient-to-r from-amber-200 to-amber-500 rounded" />
          <span className="text-slate-600">Connection strength</span>
        </div>
      </div>
    </div>
  );
}
